import React from 'react';
import { ComboBoxField, ComboBoxItem, type ComboBoxFieldProps } from './ComboBoxField';
import type { Key } from 'react-aria-components';
import { type AutocompleteOptionType, type AutocompleteTypeToModel, type FormControlOptionModel } from '@generated';
import { useAutocomplete } from '~/util/http/useAutocomplete';
import type { SieveFilterModel } from '~/util/sieve/filter';
import { useState } from 'react';

export interface AutocompleteSearchFieldProps<K extends AutocompleteOptionType>
	extends Omit<ComboBoxFieldProps<FormControlOptionModel>, 'value' | 'children' | 'required'> {
	searchType: K;
	additionalFilters?: SieveFilterModel[];
	onChange: (value: AutocompleteTypeToModel[K]) => void;
}

export function AutocompletePicklistSearchField<K extends AutocompleteOptionType>(props: AutocompleteSearchFieldProps<K>) {
	const [searchTerm, setSearchTerm] = useState('');
	const { options, isLoading, isError, getSelectedOption, getSelectedData } = useAutocomplete<K>({
		searchType: props.searchType,
		searchTerm,
		enabled: true,
		groupResult: false,
		additionalFilters: props.additionalFilters ?? []
	});

	const showOptions = !isLoading && !isError && options.length > 0 && searchTerm.length >= 3;
	const showNoOptions = !isLoading && !isError && options.length === 0 && searchTerm.length >= 3;

	const onChange = (selected: Key | null) => {
		if (!selected) {
			return;
		}
		props.onChange(getSelectedData(selected.toString()));
		setSearchTerm('');
	};

	return (
		<ComboBoxField
			{...props}
			hideChevron
			onSelectionChange={onChange}
			items={showOptions ? options : []}
			inputValue={searchTerm}
			onInputChange={setSearchTerm}
			allowsEmptyCollection
			noOptionsMessage={showNoOptions ? props.noOptionsMessage ?? 'No options found' : undefined}
		>
			{item => <ComboBoxItem id={item.value}>{item.label}</ComboBoxItem>}
		</ComboBoxField>
	);
}
